import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Route, Globe, ShoppingCart, Ticket, TrendingDown, Search, Loader2,
  MousePointerClick, Eye, PlusCircle, MessageCircle, CreditCard, CheckCircle2,
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { apiFetch, parseJson, type ApiResponse } from '../lib/api';

// ============================================================
// 客戶蹤跡（/admin/journeys）
// Tab 1 群體蹤跡：來源×結果矩陣、漏斗、優惠時點、放棄熱點（讀夜間 journey_visits 彙總）
// Tab 2 客戶時間線：電話 → web 事件＋訂單＋優惠領取/兌換合併時間線
// ============================================================

interface SourceRow {
  source: string;
  visits: number;
  ordered: number;
  claimedOnly: number;
  abandoned: number;
  claims: number;
  conversionRate: string;
}

interface SummaryData {
  days: number;
  totals: { visits: number; ordered: number; claimedOnly: number; abandoned: number; conversionRate: string };
  sources: SourceRow[];
  funnel: { step: string; visits: number }[];
  offerTiming: { beforeOrder: number; afterOrder: number };
  abandonHotspots: { step: string; count: number }[];
}

interface TimelineEntry {
  type: 'web' | 'order' | 'claim' | 'redeem';
  at: number;
  title: string;
  detail?: string;
}

interface TimelineData {
  phone: string;
  bridged: boolean;
  eventCount: number;
  timeline: TimelineEntry[];
}

const STEP_LABELS: Record<string, string> = {
  session_entry: '入站',
  product_view: '看產品',
  add_to_cart: '加入購物車',
  'whatsapp_redirect(offer_claim)': '跳轉領優惠',
  begin_checkout: '開始結帳',
  'whatsapp_redirect(order_confirm)': '跳轉確認訂單',
  order_created: '落單',
  order_paid: '已收款',
};

const WEB_EVENT_LABELS: Record<string, string> = {
  session_entry: '入站（新到訪）',
  page_view: '瀏覽頁面',
  product_view: '看產品',
  add_to_cart: '加入購物車',
  begin_checkout: '開始結帳',
  order_created: '落單（web 事件）',
  whatsapp_redirect: '跳轉 WhatsApp',
  whatsapp_click: '點擊 WhatsApp',
  offer_impression: '看到優惠',
  offer_claim_click: '點擊領取優惠',
  offer_share: '分享優惠',
  referral_link_click: '推薦連結點擊',
  popup_impression: '看到彈窗',
  popup_click: '點擊彈窗',
  popup_close: '關閉彈窗',
};

function stepLabel(step: string): string {
  return STEP_LABELS[step] || step;
}

function formatTs(ts: number): string {
  return new Date(ts * 1000).toLocaleString('zh-HK', { hour12: false });
}

function EntryIcon({ type, title }: { type: TimelineEntry['type']; title: string }) {
  const cls = 'w-8 h-8 rounded-full flex items-center justify-center shrink-0';
  if (type === 'order') return <span className={`${cls} bg-green-100 dark:bg-green-900/40`}><ShoppingCart size={16} className="text-green-700 dark:text-green-400" /></span>;
  if (type === 'claim') return <span className={`${cls} bg-amber-100 dark:bg-amber-900/40`}><Ticket size={16} className="text-amber-700 dark:text-amber-400" /></span>;
  if (type === 'redeem') return <span className={`${cls} bg-purple-100 dark:bg-purple-900/40`}><CheckCircle2 size={16} className="text-purple-700 dark:text-purple-400" /></span>;
  if (title === 'session_entry') return <span className={`${cls} bg-blue-100 dark:bg-blue-900/40`}><Globe size={16} className="text-blue-700 dark:text-blue-400" /></span>;
  if (title === 'whatsapp_redirect' || title === 'whatsapp_click') return <span className={`${cls} bg-emerald-100 dark:bg-emerald-900/40`}><MessageCircle size={16} className="text-emerald-700 dark:text-emerald-400" /></span>;
  if (title === 'add_to_cart') return <span className={`${cls} bg-orange-100 dark:bg-orange-900/40`}><PlusCircle size={16} className="text-orange-700 dark:text-orange-400" /></span>;
  if (title === 'order_created') return <span className={`${cls} bg-green-100 dark:bg-green-900/40`}><CreditCard size={16} className="text-green-700 dark:text-green-400" /></span>;
  return <span className={`${cls} bg-gray-100 dark:bg-gray-700`}><MousePointerClick size={16} className="text-gray-600 dark:text-gray-300" /></span>;
}

export default function AdminJourneys() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'summary' | 'timeline'>('summary');
  const [days, setDays] = useState(30);

  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState('');

  const [phoneInput, setPhoneInput] = useState('');
  const [timeline, setTimeline] = useState<TimelineData | null>(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState('');

  const fetchSummary = async (d: number) => {
    setSummaryLoading(true);
    setSummaryError('');
    try {
      const res = await apiFetch(`/api/admin/journeys/summary?days=${d}`);
      if (res.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
        return;
      }
      const json = await parseJson<ApiResponse<SummaryData>>(res);
      if (res.ok && json.success && json.data) {
        setSummary(json.data);
      } else {
        setSummaryError(json.error || `HTTP ${res.status}`);
      }
    } catch {
      setSummaryError('網絡錯誤');
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    fetchSummary(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const searchTimeline = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = phoneInput.trim();
    if (!trimmed) return;
    setTimelineLoading(true);
    setTimelineError('');
    setTimeline(null);
    try {
      const res = await apiFetch(`/api/admin/journeys/timeline/${encodeURIComponent(trimmed)}`);
      if (res.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
        return;
      }
      const json = await parseJson<ApiResponse<TimelineData>>(res);
      if (res.ok && json.success && json.data) {
        setTimeline(json.data);
      } else {
        setTimelineError(json.error || '查無資料');
      }
    } catch {
      setTimelineError('網絡錯誤');
    } finally {
      setTimelineLoading(false);
    }
  };

  const maxFunnel = useMemo(() => Math.max(1, ...(summary?.funnel.map((f) => f.visits) || [1])), [summary]);
  const maxHotspot = useMemo(() => Math.max(1, ...(summary?.abandonHotspots.map((h) => h.count) || [1])), [summary]);

  return (
    <AdminLayout currentPage="journeys">
      <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Route size={26} className="text-red-600" /> 客戶蹤跡
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            入站來源 → 關鍵步驟 → 結果（購買／領優惠／放棄）——每晚自動彙總，電話級時間線經訂單橋接
          </p>
        </div>
        <div className="flex rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setTab('summary')}
            className={`px-4 py-2 text-sm font-bold ${tab === 'summary' ? 'bg-red-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
          >
            群體蹤跡
          </button>
          <button
            onClick={() => setTab('timeline')}
            className={`px-4 py-2 text-sm font-bold ${tab === 'timeline' ? 'bg-red-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
          >
            客戶時間線
          </button>
        </div>
      </div>

      {tab === 'summary' && (
        <div className="space-y-6">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 dark:text-gray-400">統計範圍</span>
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1.5 text-sm rounded-lg font-medium ${days === d ? 'bg-red-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
              >
                近 {d} 日
              </button>
            ))}
          </div>

          {summaryLoading && (
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400"><Loader2 size={18} className="animate-spin" /> 載入中…</div>
          )}
          {summaryError && <div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm">{summaryError}</div>}

          {summary && !summaryLoading && (
            <>
              {/* KPI */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {[
                  { label: '到訪次數', value: summary.totals.visits, icon: <Eye size={18} />, color: 'text-blue-600' },
                  { label: '落單', value: summary.totals.ordered, icon: <ShoppingCart size={18} />, color: 'text-green-600' },
                  { label: '領優惠未落單', value: summary.totals.claimedOnly, icon: <Ticket size={18} />, color: 'text-amber-600' },
                  { label: '放棄', value: summary.totals.abandoned, icon: <TrendingDown size={18} />, color: 'text-gray-500' },
                  { label: '到訪→落單率', value: summary.totals.conversionRate, icon: <Route size={18} />, color: 'text-red-600' },
                ].map((kpi) => (
                  <div key={kpi.label} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                    <div className={`flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 ${kpi.color}`}>
                      {kpi.icon}
                      <span className="text-gray-500 dark:text-gray-400">{kpi.label}</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{kpi.value}</div>
                  </div>
                ))}
              </div>

              {/* 來源 × 結果矩陣 */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Globe size={18} className="text-red-600" /> 入站來源 × 結果
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700/50 text-gray-500 dark:text-gray-300">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium">來源</th>
                        <th className="px-3 py-2 text-right font-medium">到訪</th>
                        <th className="px-3 py-2 text-right font-medium">落單</th>
                        <th className="px-3 py-2 text-right font-medium">領優惠</th>
                        <th className="px-3 py-2 text-right font-medium">放棄</th>
                        <th className="px-4 py-2 text-right font-medium">轉化率</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {summary.sources.length === 0 && (
                        <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">尚無彙總數據（每晚 01:00 HKT 自動生成）</td></tr>
                      )}
                      {summary.sources.map((s) => (
                        <tr key={s.source} className="text-gray-700 dark:text-gray-200">
                          <td className="px-4 py-2.5 font-medium break-all max-w-[220px]">{s.source}</td>
                          <td className="px-3 py-2.5 text-right">{s.visits}</td>
                          <td className="px-3 py-2.5 text-right text-green-600 dark:text-green-400 font-medium">{s.ordered}</td>
                          <td className="px-3 py-2.5 text-right text-amber-600 dark:text-amber-400 font-medium">{s.claims}</td>
                          <td className="px-3 py-2.5 text-right text-gray-400">{s.abandoned}</td>
                          <td className="px-4 py-2.5 text-right font-bold">{s.conversionRate}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {/* 漏斗 */}
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                  <div className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <Route size={18} className="text-red-600" /> 旅程漏斗（到訪數）
                  </div>
                  <div className="space-y-2.5">
                    {summary.funnel.map((f) => (
                      <div key={f.step}>
                        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                          <span>{stepLabel(f.step)}</span>
                          <span className="font-medium text-gray-700 dark:text-gray-200">{f.visits}</span>
                        </div>
                        <div className="h-2.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div className="h-full bg-red-600 rounded-full transition-all" style={{ width: `${(f.visits / maxFunnel) * 100}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  {/* 優惠時點 */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                    <div className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                      <Ticket size={18} className="text-red-600" /> 優惠時點（領取 vs 首次落單）
                    </div>
                    {summary.offerTiming.beforeOrder + summary.offerTiming.afterOrder === 0 ? (
                      <p className="text-sm text-gray-400">尚無同時有領取與落單記錄的電話</p>
                    ) : (
                      <div className="space-y-2.5">
                        <div>
                          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                            <span>🍜 落單前已領取（優惠帶動）</span>
                            <span className="font-medium text-gray-700 dark:text-gray-200">{summary.offerTiming.beforeOrder}</span>
                          </div>
                          <div className="h-2.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div className="h-full bg-amber-500 rounded-full" style={{ width: `${(summary.offerTiming.beforeOrder / Math.max(1, summary.offerTiming.beforeOrder + summary.offerTiming.afterOrder)) * 100}%` }} />
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                            <span>🛒 落單後才領取（留客手段）</span>
                            <span className="font-medium text-gray-700 dark:text-gray-200">{summary.offerTiming.afterOrder}</span>
                          </div>
                          <div className="h-2.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div className="h-full bg-green-600 rounded-full" style={{ width: `${(summary.offerTiming.afterOrder / Math.max(1, summary.offerTiming.beforeOrder + summary.offerTiming.afterOrder)) * 100}%` }} />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 放棄熱點 */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                    <div className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                      <TrendingDown size={18} className="text-red-600" /> 放棄熱點（最後步驟）
                    </div>
                    {summary.abandonHotspots.length === 0 ? (
                      <p className="text-sm text-gray-400">無放棄到訪</p>
                    ) : (
                      <div className="space-y-2">
                        {summary.abandonHotspots.slice(0, 6).map((h) => (
                          <div key={h.step} className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 dark:text-gray-400 w-28 shrink-0 truncate">{stepLabel(h.step)}</span>
                            <div className="flex-1 h-2.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                              <div className="h-full bg-gray-400 dark:bg-gray-500 rounded-full" style={{ width: `${(h.count / maxHotspot) * 100}%` }} />
                            </div>
                            <span className="text-xs font-medium text-gray-700 dark:text-gray-200 w-8 text-right">{h.count}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {tab === 'timeline' && (
        <div className="space-y-4">
          <form onSubmit={searchTimeline} className="flex gap-2 max-w-md">
            <input
              value={phoneInput}
              onChange={(e) => setPhoneInput(e.target.value)}
              placeholder="電話號碼（如 852xxxxxxxx 或 9xxxxxxx）"
              className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-600"
            />
            <button
              type="submit"
              disabled={timelineLoading}
              className="px-5 py-2.5 rounded-xl bg-red-600 text-white font-bold hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
            >
              {timelineLoading ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
              查蹤跡
            </button>
          </form>

          {timelineError && <div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm">{timelineError}</div>}

          {timeline && (
            <>
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className="font-bold text-gray-900 dark:text-white">{timeline.phone}</span>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${timeline.bridged ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300'}`}>
                  {timeline.bridged ? '✓ 已橋接 web 旅程' : '未橋接（無帶 web ID 的訂單）'}
                </span>
                <span className="text-gray-400">共 {timeline.eventCount} 條記錄</span>
              </div>

              {timeline.timeline.length === 0 ? (
                <div className="p-8 text-center text-gray-400 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                  此電話暫無任何訂單／優惠／web 事件記錄
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 md:p-6 max-h-[65vh] overflow-y-auto">
                  <div className="space-y-0">
                    {timeline.timeline.map((entry, idx) => (
                      <div key={idx} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <EntryIcon type={entry.type} title={entry.title} />
                          {idx < timeline.timeline.length - 1 && <div className="w-px flex-1 bg-gray-200 dark:bg-gray-700 my-1" />}
                        </div>
                        <div className="pb-6 min-w-0">
                          <div className="flex flex-wrap items-baseline gap-x-2">
                            <span className="font-medium text-gray-900 dark:text-white text-sm">
                              {entry.type === 'web' ? (WEB_EVENT_LABELS[entry.title] || entry.title) : entry.title}
                            </span>
                            <span className="text-xs text-gray-400">{formatTs(entry.at)}</span>
                          </div>
                          {entry.detail && <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 break-all">{entry.detail}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
      </div>
    </AdminLayout>
  );
}
